import {
  Children,
  Fragment,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Portal from "../../portal/Portal";
import styles from "./selectInput.module.css";

const normalizeValue = (value) => {
  if (value === undefined || value === null) return "";

  return String(value);
};

const getNodeText = (nodes) =>
  Children.toArray(nodes)
    .map((node) => {
      if (typeof node === "string" || typeof node === "number") {
        return String(node);
      }

      if (isValidElement(node)) {
        return getNodeText(node.props.children);
      }

      return "";
    })
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

const collectOptions = (nodes) =>
  Children.toArray(nodes).flatMap((node) => {
    if (!isValidElement(node)) return [];

    if (node.type === Fragment) {
      return collectOptions(node.props.children);
    }

    if (node.type !== "option") return [];

    return [
      {
        value: normalizeValue(node.props.value),
        label: getNodeText(node.props.children),
        disabled: Boolean(node.props.disabled),
      },
    ];
  });

const SelectInput = ({
  setValue,
  value,
  placeHolder,
  children,
  wrapperClassName = "",
  noPlaceHolder = false,
  className = "",
  menuClassName = "",
  disabled = false,
  searchable = true,
  searchThreshold = 6,
  searchPlaceholder = "ابحث عن خيار",
  emptyResultsLabel = "لا توجد نتائج مطابقة",
}) => {
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [menuPosition, setMenuPosition] = useState({
    left: 0,
    top: 0,
    width: 0,
    maxHeight: 320,
    openUpward: false,
  });

  const options = useMemo(() => {
    const nextOptions = collectOptions(children);

    if (!noPlaceHolder) {
      nextOptions.unshift({
        value: "",
        label: placeHolder || "اختيار",
        disabled: false,
        isPlaceholder: true,
      });
    }

    return nextOptions;
  }, [children, noPlaceHolder, placeHolder]);

  const normalizedValue = normalizeValue(value);
  const selectedOption =
    options.find((option) => option.value === normalizedValue) || null;

  const visibleOptionsCount = options.filter(
    (option) => !option.isPlaceholder,
  ).length;
  const showSearch = searchable && visibleOptionsCount >= searchThreshold;

  const filteredOptions = useMemo(() => {
    const normalizedQuery = searchValue.trim().toLowerCase();

    if (!normalizedQuery) return options;

    return options.filter((option) => {
      if (option.isPlaceholder) return false;

      return option.label.toLowerCase().includes(normalizedQuery);
    });
  }, [options, searchValue]);

  const triggerLabel =
    selectedOption?.label ||
    placeHolder ||
    options.find((option) => !option.isPlaceholder)?.label ||
    "";

  const updateMenuPosition = useCallback(() => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const spacing = 8;
    const preferredMaxHeight = 320;
    const spaceBelow = window.innerHeight - rect.bottom - spacing;
    const spaceAbove = rect.top - spacing;
    const openUpward = spaceBelow < 220 && spaceAbove > spaceBelow;
    const availableSpace = Math.max(
      openUpward ? spaceAbove : spaceBelow,
      120,
    );
    const maxHeight = Math.min(preferredMaxHeight, availableSpace);

    setMenuPosition({
      left: rect.left,
      top: openUpward ? rect.top - spacing : rect.bottom + spacing,
      width: rect.width,
      maxHeight,
      openUpward,
    });
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setSearchValue("");
  }, []);

  const handleToggleMenu = () => {
    if (disabled) return;

    setIsOpen((prev) => {
      const next = !prev;

      setSearchValue("");

      return next;
    });
  };

  const handleSelect = (nextValue) => {
    if (typeof setValue === "function") {
      setValue(nextValue);
    }

    closeMenu();
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    updateMenuPosition();

    const handlePointerDown = (event) => {
      const target = event.target;

      if (
        wrapperRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }

      closeMenu();
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    const handleViewportChange = () => {
      updateMenuPosition();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [closeMenu, isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen || !showSearch) return;

    searchInputRef.current?.focus();
  }, [isOpen, showSearch]);

  return (
    <div className={`${styles.wrapper} ${wrapperClassName}`} ref={wrapperRef}>
      <button
        type="button"
        className={`${styles.container} ${className} ${
          !selectedOption || selectedOption.isPlaceholder
            ? styles.placeholder
            : ""
        } ${disabled ? styles.disabled : ""}`}
        onClick={handleToggleMenu}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        disabled={disabled}
      >
        <span className={styles.label}>{triggerLabel}</span>
        <span
          className={`${styles.caret} ${isOpen ? styles.caretOpen : ""}`}
          aria-hidden="true"
        >
          ▾
        </span>
      </button>

      {isOpen ? (
        <Portal>
          <div
            ref={menuRef}
            className={`${styles.menu} ${
              menuPosition.openUpward ? styles.menuUpward : ""
            } ${menuClassName}`}
            style={{
              left: `${menuPosition.left}px`,
              top: `${menuPosition.top}px`,
              width: `${menuPosition.width}px`,
              "--select-menu-max-height": `${menuPosition.maxHeight}px`,
            }}
          >
            {showSearch ? (
              <div className={styles.searchWrapper}>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  className={styles.searchInput}
                  placeholder={searchPlaceholder}
                  autoComplete="off"
                />
              </div>
            ) : null}

            <div className={styles.optionsList} role="listbox">
              {filteredOptions.length === 0 ? (
                <div className={styles.emptyState}>{emptyResultsLabel}</div>
              ) : (
                filteredOptions.map((option) => (
                  <button
                    key={`${option.value}-${option.label}`}
                    type="button"
                    className={`${styles.option} ${
                      option.value === normalizedValue ? styles.optionActive : ""
                    } ${option.isPlaceholder ? styles.optionPlaceholder : ""}`}
                    onClick={() => handleSelect(option.value)}
                    disabled={option.disabled}
                    role="option"
                    aria-selected={option.value === normalizedValue}
                  >
                    {option.label}
                  </button>
                ))
              )}
            </div>
          </div>
        </Portal>
      ) : null}
    </div>
  );
};

export default SelectInput;
